import React, { useState, useMemo } from 'react';
import { Text, Icon } from '../ui/index.jsx';

/**
 * Build tree structure from flat file paths
 * @param {Object} files - { 'src/App.jsx': 'content', ... }
 * @returns {Object} - Nested tree structure
 */
function buildTree(files) {
  const tree = {};

  Object.entries(files).forEach(([path, content]) => {
    const parts = path.split('/');
    let current = tree;

    parts.forEach((part, index) => {
      if (index === parts.length - 1) {
        // File
        current[part] = {
          type: 'file',
          path,
          content: content || '',
        };
      } else {
        // Directory
        if (!current[part]) {
          current[part] = {
            type: 'directory',
            children: {}
          };
        }
        current = current[part].children;
      }
    });
  });

  return tree;
}

/**
 * Get file icon based on extension
 */
function getFileIcon(filename) {
  const ext = filename.split('.').pop()?.toLowerCase();
  const iconMap = {
    'jsx': { icon: 'file-code', color: 'text-blue-400' },
    'js': { icon: 'file-code', color: 'text-yellow-400' },
    'ts': { icon: 'file-code', color: 'text-blue-500' },
    'tsx': { icon: 'file-code', color: 'text-blue-500' },
    'css': { icon: 'file-code', color: 'text-pink-400' },
    'html': { icon: 'file-code', color: 'text-orange-400' },
    'json': { icon: 'file-json', color: 'text-yellow-300' },
    'md': { icon: 'file-text', color: 'text-gray-400' },
    'toml': { icon: 'file-cog', color: 'text-gray-500' },
    'svg': { icon: 'image', color: 'text-green-400' },
    'png': { icon: 'image', color: 'text-purple-400' },
    'ico': { icon: 'image', color: 'text-purple-400' }
  };
  return iconMap[ext] || { icon: 'file', color: 'text-gray-400' };
}

/**
 * Status icon component
 */
function StatusIcon({ status }) {
  switch (status) {
    case 'generating':
      return (
        <span className="animate-spin text-[#7B61FF]" title="Generating...">
          <Icon name="loader" size="xs" />
        </span>
      );
    case 'pending':
      return <span className="text-gray-500 text-xs" title="Pending">⏳</span>;
    case 'completed':
    default:
      return <span className="text-green-400 text-xs" title="Complete">✓</span>;
  }
}

/**
 * TreeNode component - renders a single node (file or directory)
 */
function TreeNode({ name, node, depth = 0, onFileSelect, selectedFile, currentFileName }) {
  const [isExpanded, setIsExpanded] = useState(depth < 2); // Auto-expand first 2 levels

  const isDirectory = node.type === 'directory';
  const isSelected = !isDirectory && node.path === selectedFile;
  const isGenerating = !isDirectory && node.path === currentFileName;
  const paddingLeft = depth * 12;

  if (isDirectory) {
    const children = Object.entries(node.children || {}).sort(([a, nodeA], [b, nodeB]) => {
      // Directories first, then files
      if (nodeA.type === 'directory' && nodeB.type !== 'directory') return -1;
      if (nodeA.type !== 'directory' && nodeB.type === 'directory') return 1;
      return a.localeCompare(b);
    });

    return (
      <div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-1.5 py-1 px-2 hover:bg-[#2A2A3C]/50 rounded text-left transition-colors"
          style={{ paddingLeft: `${paddingLeft + 8}px` }}
        >
          <Icon
            name={isExpanded ? 'chevron-down' : 'chevron-right'}
            size="xs"
            className="text-gray-500 flex-shrink-0"
          />
          <Icon
            name={isExpanded ? 'folder-open' : 'folder'}
            size="xs"
            className="text-yellow-500 flex-shrink-0"
          />
          <span className="text-sm text-gray-300 truncate">{name}</span>
        </button>

        {isExpanded && (
          <div>
            {children.map(([childName, childNode]) => (
              <TreeNode
                key={childName}
                name={childName}
                node={childNode}
                depth={depth + 1}
                onFileSelect={onFileSelect}
                selectedFile={selectedFile}
                currentFileName={currentFileName}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // File node
  const fileIcon = getFileIcon(name);
  const status = isGenerating ? 'generating' : 'completed';

  return (
    <button
      onClick={() => onFileSelect(node.path, node.content)}
      className={`w-full flex items-center gap-1.5 py-1 px-2 rounded text-left transition-colors ${
        isSelected
          ? 'bg-[#7B61FF]/20 text-white'
          : isGenerating
          ? 'bg-[#7B61FF]/10 text-[#BA87FF]'
          : 'hover:bg-[#2A2A3C]/50 text-gray-400'
      }`}
      style={{ paddingLeft: `${paddingLeft + 8}px` }}
    >
      <Icon name={fileIcon.icon} size="xs" className={`flex-shrink-0 ${fileIcon.color}`} />
      <span className={`text-sm truncate flex-1 ${isSelected || isGenerating ? 'text-white' : ''}`}>
        {name}
      </span>
      <StatusIcon status={status} />
    </button>
  );
}

/**
 * FileTree component - displays generated files in a tree structure
 */
export default function FileTree({
  files = {},
  onFileSelect,
  selectedFile,
  currentFileName,
  className = ''
}) {
  const tree = useMemo(() => buildTree(files), [files]);
  const fileCount = Object.keys(files).length;

  // Sort root level entries
  const sortedEntries = useMemo(() => {
    return Object.entries(tree).sort(([a, nodeA], [b, nodeB]) => {
      if (nodeA.type === 'directory' && nodeB.type !== 'directory') return -1;
      if (nodeA.type !== 'directory' && nodeB.type === 'directory') return 1;
      return a.localeCompare(b);
    });
  }, [tree]);

  if (fileCount === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full text-gray-500 ${className}`}>
        <Icon name="folder" size="lg" className="mb-2 opacity-50" />
        <Text className="text-sm">No files generated yet</Text>
      </div>
    );
  }

  return (
    <div className={`overflow-y-auto ${className}`}>
      <div className="py-1">
        {sortedEntries.map(([name, node]) => (
          <TreeNode
            key={name}
            name={name}
            node={node}
            depth={0}
            onFileSelect={onFileSelect}
            selectedFile={selectedFile}
            currentFileName={currentFileName}
          />
        ))}
      </div>
    </div>
  );
}
